import type { NormalizedPoint } from '../model';
import type { PaperSize } from './geometry';

// #772 parts 2b and 3 -- snapping a placed or moved point (docs/model/drawing-ui-viewport.md):
// smart alignment guides (domain/snapGuides.ts), then Snap to grid. Snapping to an existing atom
// or bond, and the bond tool's angle/length proposals, are decided by the callers first and win.
// New points snap directly; a drag snaps its grabbed point and moves the rest rigidly by the same
// offset. Holding Alt/Option, or the touch "place freely" control, bypasses all of it. A
// normalized point times the paper size is in drawing units (1 unit = 1 CSS px at 100%).

export interface SnapModifiers {
  readonly altKey?: boolean;
  // Items being dragged: a drag never aligns with itself (atom and annotation ids).
  readonly exclude?: ReadonlySet<string>;
  // Snap without showing guide/target feedback (drops and paste, which have no gesture to follow).
  readonly quiet?: boolean;
}

// Snaps a paper-normalized point; returns the point unchanged when nothing applies or bypassed.
export type SnapPoint = (point: NormalizedPoint, modifiers?: SnapModifiers) => NormalizedPoint;

export const NO_SNAP: SnapPoint = (point) => point;

export function snapUnitsToGrid(value: number, spacing: number): number {
  return Math.round(value / spacing) * spacing;
}

export function snapNormalizedToGrid(point: NormalizedPoint, spacing: number, size: PaperSize): NormalizedPoint {
  if (!(spacing > 0) || !(size.width > 0) || !(size.height > 0)) return point;
  return {
    x: snapUnitsToGrid(point.x * size.width, spacing) / size.width,
    y: snapUnitsToGrid(point.y * size.height, spacing) / size.height
  };
}

// The offset that lands a drag's grabbed point on the grid: the grabbed point's start plus the raw
// pointer offset, snapped, minus its start. Applied to every dragged item, so a group stays rigid.
export function snappedDragOffset(
  grabbed: NormalizedPoint,
  offset: NormalizedPoint,
  snap: SnapPoint,
  modifiers?: SnapModifiers
): NormalizedPoint {
  const target = snap({ x: grabbed.x + offset.x, y: grabbed.y + offset.y }, modifiers);
  return { x: target.x - grabbed.x, y: target.y - grabbed.y };
}
